import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import { Users, BarChart3 } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

const UsersDetails = () => {
  const [visitorCount, setVisitorCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [weeklyData, setWeeklyData] = useState([]);
  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      setLoading(true);
      const userToken = localStorage.getItem("adminToken");
      if (!userToken) throw new Error("No token found. Please log in.");

      // Fetch total visitor count
      const visitorResponse = await fetch(
        `${SERVER_URL}/api/visitors/visitor-count`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userToken}`,
          },
        },
      );

      if (!visitorResponse.ok) {
        const errorMessage = await visitorResponse.text();
        throw new Error(
          `Failed to fetch visitor count: ${errorMessage} (Status: ${visitorResponse.status})`,
        );
      }

      const visitorData = await visitorResponse.json();
      setVisitorCount(visitorData.visitorCount || 0);

      // Fetch weekly stats for chart
      const chartResponse = await fetch(
        `${SERVER_URL}/api/visitors/weekly-stats`,
        {
          headers: {
            Authorization: `Bearer ${userToken}`,
          },
        },
      );

      if (!chartResponse.ok) {
        throw new Error("Failed to fetch weekly stats.");
      }

      const chartData = await chartResponse.json();
      setWeeklyData(chartData);
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []); // Fetch the visitor count data when the component mounts

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    setVisitorCount(0);
    setError("Session expired. Please log in again.");
    navigate("/admin");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <CircularProgress size={40} sx={{ color: "#eab308" }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <div className="bg-red-500/10 border border-red-500/30 text-red-600 px-6 py-4 rounded-2xl shadow-lg backdrop-blur-sm">
          <strong>Error:</strong> {error}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-7xl mx-auto space-y-8 animate-in fade-in zoom-in-95 duration-500">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black text-gray-900 tracking-tight">
            Users Overview
          </h1>
          <p className="text-gray-500 mt-1">
            Real-time analytics and visitor tracking
          </p>
        </div>
        <button
          onClick={handleLogout}
          className="hidden md:flex items-center gap-2 bg-white border border-gray-200 text-gray-700 px-5 py-2.5 rounded-full font-semibold hover:border-red-200 hover:text-red-500 hover:bg-red-50 transition-all shadow-sm"
        >
          Logout Session
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Total Visitors Card */}
        <div className="col-span-1 bg-white border border-gray-100 rounded-[2rem] p-7 shadow-[0_8px_30px_rgb(0,0,0,0.02)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.06)] hover:-translate-y-1 transition-all duration-500 relative overflow-hidden group cursor-default">
          <div className="absolute top-0 right-0 p-8 translate-x-4 -translate-y-4 opacity-5 group-hover:opacity-10 transition-opacity duration-700">
            <Users className="w-32 h-32 text-yellow-600 scale-110" />
          </div>
          <div className="relative z-10 flex flex-col h-full justify-between">
            <div className="flex items-start justify-between">
              <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-2xl flex items-center justify-center shadow-lg shadow-yellow-500/20 mb-6 transition-transform duration-500 group-hover:scale-110">
                <Users className="text-white w-6 h-6" />
              </div>
              <div className="text-right">
                <span className="text-[10px] font-black text-yellow-600 bg-yellow-50 px-2 py-1 rounded-full uppercase tracking-tighter">
                  Real-time
                </span>
              </div>
            </div>
            <div>
              <h2 className="text-gray-400 font-bold text-xs uppercase tracking-widest">
                Total Visitors
              </h2>
              <p className="text-5xl font-black text-gray-900 mt-1 tracking-tight group-hover:text-yellow-600 transition-colors duration-500">
                {visitorCount.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        {/* Live Analytics Hero Card */}
        <div className="col-span-1 md:col-span-2 bg-gradient-to-br from-gray-900 to-gray-950 rounded-[2rem] p-8 shadow-2xl relative overflow-hidden flex items-center justify-between group">
          {/* Subtle animated border glow */}
          <div className="absolute inset-0 border border-white/5 rounded-[2rem]"></div>

          <div className="absolute right-0 top-0 w-80 h-80 bg-yellow-500/10 rounded-full blur-[100px] pointer-events-none translate-x-1/3 -translate-y-1/3 group-hover:bg-yellow-500/20 transition-all duration-700" />

          <div className="relative z-10 space-y-5">
            <div className="inline-flex items-center gap-2.5 px-3.5 py-1.5 bg-white/5 backdrop-blur-md border border-white/10 rounded-full text-yellow-400 text-xs font-bold tracking-wide">
              <div className="relative flex items-center justify-center mr-1">
                <div className="absolute w-2 h-2 rounded-full bg-yellow-500 animate-ping opacity-50" />
                <div className="w-2 h-2 rounded-full bg-yellow-500" />
              </div>
              Live Analytics Enabled
            </div>
            <div className="space-y-2">
              <h2 className="text-3xl font-black text-white max-w-sm leading-[1.1] tracking-tight">
                Audience Insights <span className="text-gray-500">&</span> Flow
                Tracking
              </h2>
              <p className="text-gray-400 text-sm max-w-xs font-medium leading-relaxed">
                Visualizing visitor trends and engagement metrics over the last
                7 days.
              </p>
            </div>
          </div>

          <div className="hidden lg:block relative z-10 mr-4">
            <div className="w-24 h-24 bg-white/5 rounded-3xl flex items-center justify-center border border-white/10 transform rotate-12 group-hover:rotate-0 transition-transform duration-700">
              <BarChart3 className="w-12 h-12 text-yellow-500/40 group-hover:text-yellow-500 transition-colors duration-700" />
            </div>
          </div>
        </div>
      </div>

      {/* Bar Chart */}
      <div className="bg-white border border-gray-100 rounded-[2rem] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <div className="px-2 mb-6">
          <h2 className="text-xl font-bold text-gray-900">Weekly Visitors</h2>
          <p className="text-gray-500 text-sm">Traffic over the last 7 days</p>
        </div>
        <div style={{ width: "100%", height: 350 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={weeklyData}
              margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
            >
              <defs>
                <linearGradient id="colorVisitors" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#eab308" stopOpacity={1} />
                  <stop offset="95%" stopColor="#ca8a04" stopOpacity={0.8} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="#f3f4f6"
              />
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={{ fill: "#6b7280", fontSize: 13 }}
                dy={10}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: "#6b7280", fontSize: 13 }}
              />
              <Tooltip
                cursor={{ fill: "#f9fafb" }}
                contentStyle={{
                  borderRadius: "16px",
                  border: "none",
                  boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
                  padding: "12px 20px",
                  fontWeight: "bold",
                }}
              />
              <Bar
                dataKey="visitors"
                fill="url(#colorVisitors)"
                radius={[6, 6, 0, 0]}
                barSize={40}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default UsersDetails;
